import React from 'react'

import {
  pantryColors,
  pantryRadii,
  pantryTypography,
} from '../../../foundations/tokens'

type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'danger-ghost' | 'icon'
type ButtonSize = 'sm' | 'md'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: React.ReactNode
  children?: React.ReactNode
}

const baseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  borderRadius: pantryRadii.sm,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  border: 'none',
  transition: 'all 0.15s',
  fontFamily: pantryTypography.fontFamily,
  lineHeight: 1,
}

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  md: { padding: '8px 14px' },
  sm: { padding: '5px 10px', fontSize: 12 },
}

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: pantryColors.brand.primary,
    color: pantryColors.white,
  },
  ghost: {
    background: 'transparent',
    color: pantryColors.gray[600],
    border: `1px solid ${pantryColors.gray[300]}`,
  },
  danger: {
    background: 'transparent',
    color: pantryColors.red[700],
    border: `1px solid ${pantryColors.red[700]}`,
    opacity: 0.8,
  },
  'danger-ghost': {
    background: 'transparent',
    color: pantryColors.red[700],
    border: `1px solid ${pantryColors.red[200]}`,
  },
  icon: {
    width: 32,
    height: 32,
    padding: 0,
    background: 'transparent',
    color: pantryColors.gray[500],
  },
}

const hoverStyles: Record<ButtonVariant, Partial<React.CSSProperties>> = {
  primary: { background: pantryColors.brand.hover },
  ghost: { background: pantryColors.gray[100], color: pantryColors.gray[800] },
  danger: { background: pantryColors.red[50], opacity: 1 },
  'danger-ghost': { background: pantryColors.red[50] },
  icon: { background: pantryColors.gray[200], color: pantryColors.gray[700] },
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'ghost',
  size = 'md',
  icon,
  children,
  style,
  onMouseEnter,
  onMouseLeave,
  disabled,
  ...props
}) => {
  const combined: React.CSSProperties = {
    ...baseStyle,
    ...sizeStyles[variant === 'icon' ? 'md' : size],
    ...variantStyles[variant],
    ...(disabled
      ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }
      : {}),
    ...style,
  }

  return (
    <button
      {...props}
      disabled={disabled}
      style={combined}
      onMouseEnter={(e) => {
        if (!disabled) {
          Object.assign(e.currentTarget.style, hoverStyles[variant])
        }
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          Object.assign(e.currentTarget.style, variantStyles[variant])
        }
        onMouseLeave?.(e)
      }}
    >
      {icon && (
        <span
          style={{
            width: 15,
            height: 15,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
      )}
      {children}
    </button>
  )
}
